import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdBanner from '../components/AdBanner';
import { getBlogPosts } from '../services/api';
import { STATIC_POSTS_LIST } from '../data/staticBlogData';
import './BlogPage.css';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchPosts = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      const result = await getBlogPosts(pageNum, 20);
      if (result.success && result.data.length > 0) {
        setPosts(result.data);
        setTotalPages(result.pagination.totalPages);
        setPage(pageNum);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
      setPosts([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = '블로그 - 로또운세';
    fetchPosts(1);
  }, [fetchPosts]);

  // API 글이 있으면 API 글만, 없으면 정적 글만 표시
  const displayPosts = posts.length > 0 ? posts : STATIC_POSTS_LIST;

  return (
    <div className="blog-page">
      <Helmet>
        <title>블로그 - 로또운세 | 매주 로또 당첨번호 분석</title>
        <meta name="description" content="매주 로또 당첨번호 발표, 순위별 당첨금 안내, 번호 분석, 운세 이야기. 로또운세 블로그에서 최신 로또 정보를 확인하세요." />
        <meta property="og:title" content="블로그 - 로또운세" />
        <meta property="og:description" content="매주 로또 당첨번호 발표, 순위별 당첨금 안내, 번호 분석, 운세 이야기." />
        <meta property="og:url" content="https://lottounse.kr/blog" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://lottounse.kr/blog" />
      </Helmet>

      <div className="blog-header">
        <h1>로또운세 블로그</h1>
        <p>매주 로또 당첨번호 분석과 운세 이야기</p>
      </div>

      <AdBanner />

      <div className="blog-content">
        {loading ? (
          <div className="blog-loading">로딩 중...</div>
        ) : (
          <>
            <div className="blog-grid">
              {displayPosts.map((post) => {
                const emoji = post.category === '당첨결과' ? '🎱' : (post.emoji || '📝');

                return (
                  <Link to={`/blog/${post.slug}`} key={post.slug || post.id} className="blog-card">
                    <div className="blog-card-emoji">{emoji}</div>
                    <div className="blog-card-meta">
                      <span className="blog-category">{post.category}</span>
                      <span className="blog-date">{formatDate(post.published_at)}</span>
                    </div>
                    <h2 className="blog-card-title">{post.title}</h2>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <div className="blog-card-footer">
                      <span className="read-more">자세히 보기 →</span>
                    </div>
                  </Link>
                );
              })}
            </div>

            {posts.length > 0 && STATIC_POSTS_LIST.length > 0 && (
              <div className="blog-grid" style={{ marginTop: '2rem' }}>
                <h3 style={{ gridColumn: '1 / -1', marginBottom: '1rem' }}>읽을거리</h3>
                {STATIC_POSTS_LIST.map((post) => (
                  <Link to={`/blog/${post.slug}`} key={post.slug} className="blog-card">
                    <div className="blog-card-emoji">{post.emoji || '📝'}</div>
                    <div className="blog-card-meta">
                      <span className="blog-category">{post.category}</span>
                      <span className="blog-date">{formatDate(post.published_at)}</span>
                    </div>
                    <h2 className="blog-card-title">{post.title}</h2>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <div className="blog-card-footer">
                      <span className="read-more">자세히 보기 →</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}

        {totalPages > 1 && (
          <div className="blog-pagination">
            {page > 1 && (
              <button onClick={() => fetchPosts(page - 1)} className="page-btn">← 이전</button>
            )}
            <span className="page-info">{page} / {totalPages}</span>
            {page < totalPages && (
              <button onClick={() => fetchPosts(page + 1)} className="page-btn">다음 →</button>
            )}
          </div>
        )}
      </div>

      <AdBanner />

      <div className="blog-sidebar">
        <div className="sidebar-section">
          <h3>인기 주제</h3>
          <div className="tag-cloud">
            <span className="tag">로또 당첨번호</span>
            <span className="tag">당첨금 안내</span>
            <span className="tag">번호 분석</span>
            <span className="tag">별자리 운세</span>
            <span className="tag">타로 카드</span>
          </div>
        </div>

        <div className="sidebar-section">
          <h3>업데이트</h3>
          <p>매주 토요일 저녁, 최신 로또 당첨번호와 분석이 자동 업데이트됩니다.</p>
        </div>
      </div>
    </div>
  );
}

export default BlogPage;
